import { normalizeValue } from "../../../common/FiltersFormEngine/mappers/simpleToAdvancedFormState";
import { FiltersListDTO, UserCustomFiltersListDTO } from "../../../datasources/api-client/data-contracts";
import { FilterDefinition, FilterOperatorDefinition, FilterOption, FilterQuery, FiltersRegistry } from "../types";

// Builds a registry from API data
// Returns null if required sources are missing
export const buildRegistry = ({
	available,
	saved,
}: {
	available: FiltersListDTO | undefined;
	saved: UserCustomFiltersListDTO | undefined;
}): FiltersRegistry | null => {
	if (!available || !saved) return null;

	return normalizeRegistry(available, saved);
};

// REGISTRY NORMALIZATION

// Converts API data into internal FiltersRegistry structure
export const normalizeRegistry = (available: FiltersListDTO, saved: UserCustomFiltersListDTO): FiltersRegistry => {
	const items: FilterDefinition[] = [];
	const byId: Record<string, FilterDefinition> = {};

	const groups: FiltersRegistry["groups"] = {
		available: [],
		saved: [],
		configurations: [],
		favorites: [],
	};

	// Adds a filter into registry and updates all indexes
	const add = (filter: FilterDefinition, group: keyof FiltersRegistry["groups"]) => {
		items.push(filter);
		byId[filter.id] = filter;

		groups[group].push(filter.id);

		// also track favorites globally
		if (filter.favorite) {
			groups.favorites.push(filter.id);
		}
	};

	// SYSTEM FILTERS (available)
	for (const dto of available.items) {
		add(mapAvailableFilter(dto), "available");
	}

	// USER FILTERS (saved / configurations)
	for (const dto of saved.items) {
		const filter = mapSavedFilter(dto);

		add(filter, filter.filterType === "simple" ? "saved" : "configurations");
	}

	return { items, byId, groups };
};

// AVAILABLE FILTER MAPPING (SYSTEM)

// Maps API system filter into internal FilterDefinition
export const mapAvailableFilter = (dto: FiltersListDTO["items"][number]): FilterDefinition => {
	return {
		id: dto.id,

		source: "system",
		filterType: "simple",
		presentation: "chip",

		name: dto.label,
		description: undefined,

		// default empty query structure
		filter: {
			operator: "and",
			filters: [],
		} as FilterQuery,

		field: {
			id: dto.id,
			label: dto.label,
			inputType: dto.type,
			dataType: dto.dataType,

			operators: (dto.operators ?? []).map(mapOperator),
			options: (dto.options ?? []).map(mapOption),

			visibility: dto.visibility ?? [],
			favorite: Boolean(dto.isFavorite),

			defaultValue: normalizeValue(dto.preselectedValue),
		},

		visibility: dto.visibility ?? [],

		ui: {
			canPin: true,
			hasMenu: false,
		},

		favorite: Boolean(dto.isFavorite),

		group: "available",
	};
};

// SAVED FILTER MAPPING (USER)

// Maps user saved filter into internal FilterDefinition
export const mapSavedFilter = (dto: UserCustomFiltersListDTO["items"][number]): FilterDefinition => {
	const isComplex = Boolean(dto.isComplex);

	return {
		id: dto.id,

		source: "user",
		filterType: isComplex ? "configuration" : "simple",
		presentation: isComplex ? "builder" : "chip",

		name: dto.name,
		description: undefined,

		filter: dto.filter as FilterQuery,

		field: dto.field ?? undefined,

		visibility: dto.visibility ?? [],

		favorite: false,

		ui: {
			canPin: false,
			hasMenu: true,
		},

		group: isComplex ? "configurations" : "saved",
	};
};

// FIELD HELPERS

// Maps operator from API format to internal definition
export const mapOperator = (op: any): FilterOperatorDefinition => ({
	operator: op.operator,
	label: op.label,
	supportsMultiSelect: Boolean(op.supportsMultiSelect),
	argsType: op.argsType,
});

// Maps option from API format to internal definition
export const mapOption = (opt: any): FilterOption => ({
	id: String(opt.id),
	label: opt.label,
});
